import { TFunction } from "i18next";

/**
 * Survey question input placeholders (text, number, date).
 * Mobile app follow-up: read `question.properties.placeholder` when rendering
 * these field types; when empty, keep existing per-type defaults (Enter value,
 * format-based date/time strings). Translated placeholders are merged into
 * properties by form.transformer before participant API responses.
 */
const dateTimePickerFormats = ["DD/MM/YYYY HH:mm", "MM/DD/YYYY HH:mm"];
const timePickerFormats = ["HH:mm", "HH:mm:ss"];

export type DatePlaceholderContext = "builderPreview" | "responses";

export function resolveQuestionPlaceholder(
  properties: { placeholder?: string } | undefined,
  fallback: string,
): string {
  const value = properties?.placeholder;
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return fallback;
}

export function getDatePlaceholderFallback(
  format: string | undefined,
  t: TFunction,
  context: DatePlaceholderContext,
): string {
  if (format && dateTimePickerFormats.includes(format)) {
    if (context === "responses") {
      return t("TYPE_BASED.SELECT_DATE_AND_TIME", { ns: "responses" });
    }
    return t("FORM_BUILDER.PREVIEW.PLACEHOLDER_SELECT_DATE_AND_TIME", {
      ns: "study-designer",
    });
  }

  if (format && timePickerFormats.includes(format)) {
    if (context === "responses") {
      return t("TYPE_BASED.SELECT_TIME", { ns: "responses" });
    }
    return t("FORM_BUILDER.QUESTION_PROPERTIES.PLACEHOLDER_SELECT_TIME", {
      ns: "study-designer",
    });
  }

  if (context === "responses") {
    return t("TYPE_BASED.SELECT_DATE", { ns: "responses" });
  }
  return t("FORM_BUILDER.QUESTION_PROPERTIES.PLACEHOLDER_SELECT_DATE", {
    ns: "study-designer",
  });
}

export function resolveDateQuestionPlaceholder(
  properties: { placeholder?: string; format?: string } | undefined,
  t: TFunction,
  context: DatePlaceholderContext,
): string {
  return resolveQuestionPlaceholder(
    properties,
    getDatePlaceholderFallback(properties?.format, t, context),
  );
}
